import { useEffect, useState } from "react";

import { useNavigate, useLocation, createSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { setIsAuth } from "domains/authentication/store/auth.reducer";
import { URLs } from "common/lib/constants";
import { sessionService } from "common/shared";
import { UserType } from "domains/clients/shared/types";
import { useIdentitySelector } from "domains/identity/store/identity.selectors";
import { useClientsSelector } from "domains/clients/store/clients.selectors";
import { getIsAuth } from "domains/authentication/store/auth.selectors";
import { useBootstrapSelector } from "common/shared-store/bootstrap.selectors";
import { setNavigateToAfterLogin } from "common/shared-store/bootstrap.reducer";
import { useTrustbenSelector } from "domains/trustben";
import { featuresApiReducer } from "domains/features/store/features.api.reducer";
import { identityApiReducer } from "domains/identity/store/identity.api.reducer";
import { documentsApiReducer } from "domains/documents/store/documents.api.reducer";
import { clientsApiReducer } from "domains/clients/store/clients.api.reducer";
import { IErrorResponse } from "common/shared/types";
import { liquidityApiReducer } from "domains/liquidity/store/liquidity.api.reducer";
import { authApiReducer } from "domains/authentication/store/auth.api.reducer";
import { altCustodyApiReducer } from "domains/altCustody/store/altCustody.api.reducer";
import { FeatureNames, useFeature } from "domains/features";

const useInit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { isFeatureActive } = useFeature(FeatureNames.AltCustody, []);
  const { isTrustbenAltQuote } = useTrustbenSelector();
  const { data: liquidityRequests } = liquidityApiReducer.useFetchLiquidityRequestsQuery();
  const { navigateToAfterLogin } = useBootstrapSelector();
  const isAuth = useSelector(getIsAuth);

  const { user, isAdvisor } = useClientsSelector();
  const { identity } = useIdentitySelector();
  const [ fetchUserClients ] = clientsApiReducer.useLazyFetchUserClientsQuery();
  const [ fetchDocumentsByAccountId ] = documentsApiReducer.useLazyFetchDocumentsByIdQuery();
  const [ fetchFeatures ] = featuresApiReducer.useLazyFetchFeaturesQuery();
  const [ fetchIdentity ] = identityApiReducer.useLazyFetchIdentityQuery();
  const [ fetchInvestments ] = altCustodyApiReducer.useLazyFetchInvestmentsQuery();
  const [ fetchUserLevelDetails, { isError, error } ] = clientsApiReducer.useLazyFetchUserLevelDetailsQuery();
  const [ fetchAccountLevelDetailsById ] = clientsApiReducer.useLazyFetchAccountLevelDetailsByIdQuery();
  const [ fetchAllAssets ] = liquidityApiReducer.useLazyFetchAllAssetsQuery();
  const [ fetchLiquidityRequests ] = liquidityApiReducer.useLazyFetchLiquidityRequestsQuery();
  const [ logout ] = authApiReducer.useLogoutMutation();

  const [ isLoaderDisplayed, setIsLoaderDisplayed ] = useState(true);

  const isUserLoggedIn = sessionService.isAuthenticated();
  const isAuthPage = Object.values(URLs.AUTH).some((url) => pathname.startsWith(url));

  useEffect(() => {
    (async () => {
      if (sessionService.start()) {
        dispatch(setIsAuth(true));
      }
      setIsLoaderDisplayed(false);
    })();
  }, []);

  useEffect(() => {
    if (isAuth) {
      fetchIdentity();
    }
  }, [ isAuth ]);

  useEffect(() => {
    if (!isAuth) return;

    fetchFeatures();
  }, [ isAuth ]);

  useEffect(() => {
    if (identity && isAuth) {
      fetchUserLevelDetails({ accountId: identity.account_id, userId: identity.user_id, type: "user" });
    }
  }, [ identity?.email, isAuth ]);

  useEffect(() => {
    if (!isError) return;

    if ((error as IErrorResponse).status === 403) {
      logout();
      navigate(URLs.AUTH.SIGN_IN);
    }
  }, [ isError, error ]);

  useEffect(() => {
    if (user && isAuth) {
      fetchLiquidityRequests();
      
      if (process.env.NODE_ENV !== 'production' && identity) {
        const LogRocket = require('logrocket');
        LogRocket.identify(user.user_id, {
          name: `${ user.first_name || '' } ${ user.last_name || '' }`.trim() || identity.email,
          email: identity.email,
          accountId: user.account_id,
          accountName: user.account_name,
          userType: user.user_type,
          role: identity.role,
          whiteLabelInstance: user.white_label_instance,
          isAdvisor: user.user_type !== UserType.Principal,
        });
      }
    }
  }, [ user?.user_id, isAuth ]);

  useEffect(() => {
    if (user && user.user_type !== UserType.Principal) {
      fetchAccountLevelDetailsById({ accountId: user.account_id, type: "user" });
    }
  }, [ user?.user_type ]);

  useEffect(() => {
    if (!isAuth || !user?.account_id || !isFeatureActive) return;

    if (!isAdvisor) {
      fetchInvestments(user.account_id);
    }
  }, [ isAuth, isAdvisor, user?.account_id, isFeatureActive ]);

  useEffect(() => {
    if (user && isAdvisor) {
      fetchUserClients({ accountId: user.account_id, userId: user.user_id });
    }
  }, [ user?.user_id, isAdvisor ]);

  useEffect(() => {
    if (liquidityRequests && isAuth && !isTrustbenAltQuote) {
      fetchAllAssets();
    }
  }, [ !!liquidityRequests, isAuth, isTrustbenAltQuote ]);

  useEffect(() => {
    if (isAuth && user?.account_id) {
      fetchDocumentsByAccountId(user.account_id);
    }
  }, [ isAuth, user?.account_id ]);

  useEffect(() => {
    if (navigateToAfterLogin && pathname === navigateToAfterLogin.pathname && !isAuthPage) {
      dispatch(setNavigateToAfterLogin(null));
      return;
    }

    if (isAuth && isAuthPage) {
      if (navigateToAfterLogin) {
        navigateToAfterLogin.search
          ? navigate(
              {
                pathname: navigateToAfterLogin.pathname,
                search: createSearchParams(navigateToAfterLogin.search).toString(),
              },
              { replace: true },
            )
          : navigate(navigateToAfterLogin, { state: navigateToAfterLogin.state, replace: true });
      } else {
        if (isAuthPage && !isTrustbenAltQuote) {
          navigate(URLs.PROTECTED.DASHBOARD);
        }
      }
    }
  }, [ isAuth, isAuthPage, navigateToAfterLogin, isTrustbenAltQuote, navigate, dispatch, pathname ]);

  useEffect(() => {
    if (!isUserLoggedIn) {
      setIsLoaderDisplayed(false);
    }
  }, [ isUserLoggedIn ]);

  return isLoaderDisplayed;
};

export default useInit;
